import { ModelDataType } from 'src/app/interfaces/model-data-type';

import { ContractSite, SiteContact, VendorContact } from '.';
import { BaseModel } from './base-model';
import { ClientRegisteredVendor } from './client-registered-vendor';
import { Company } from './company';
import { Contact } from './contact';
import { ExemptVendor } from './exempt-vendor';
import { File } from './file';
import { FrequencyPeriod } from './frequency-period';
import { Site } from './site';
import { Vendor } from './vendor';

export class Contract extends BaseModel {
  static override columnMap = new Map<
    string,
    { property: string; type: 'column'; dataType: ModelDataType }
  >();
  static override associationMap = new Map<
    string,
    { property: string; type: 'association'; model: string }
  >();
  static override aliasedColumnMap = new Map<
    string,
    { property: string; type: 'alias' }
  >();

  static ContractProperties: (
    | {
        property: keyof Contract;
        type: 'column';
        dataType: ModelDataType;
      }
    | {
        property: keyof Contract;
        type: 'alias';
      }
    | {
        property: keyof Contract;
        type: 'association';
        model: string;
      }
  )[] = [
    { property: 'id', type: 'column', dataType: 'number' },
    { property: 'startDate', type: 'column', dataType: 'date' },
    { property: 'expirationDate', type: 'column', dataType: 'date' },
    { property: 'contractName', type: 'column', dataType: 'string' },
    { property: 'cancellationReminderDate', type: 'column', dataType: 'date' },
    {
      property: 'cancellationDeliveryMethod',
      type: 'column',
      dataType: 'string'
    },
    { property: 'totalContractValue', type: 'column', dataType: 'number' },
    { property: 'contractFrequency', type: 'column', dataType: 'number' },
    { property: 'autoRenewing', type: 'column', dataType: 'boolean' },
    { property: 'renewalTerm', type: 'column', dataType: 'number' },
    { property: 'clientFirstName', type: 'column', dataType: 'string' },
    { property: 'clientLastName', type: 'column', dataType: 'string' },
    { property: 'clientEmail', type: 'column', dataType: 'string' },
    { property: 'clientPhone', type: 'column', dataType: 'string' },
    { property: 'isFinalized', type: 'column', dataType: 'boolean' },
    { property: 'vendorFirstName', type: 'column', dataType: 'string' },
    { property: 'vendorLastName', type: 'column', dataType: 'string' },
    { property: 'vendorCellPhone', type: 'column', dataType: 'string' },
    { property: 'vendorWorkPhone', type: 'column', dataType: 'string' },
    { property: 'vendorEmail', type: 'column', dataType: 'string' },
    { property: 'vendorContract', type: 'column', dataType: 'string' },
    { property: 'vendorContract2', type: 'column', dataType: 'string' },
    { property: 'vendorCity', type: 'column', dataType: 'string' },
    { property: 'vendorState', type: 'column', dataType: 'string' },
    { property: 'viveVendor', type: 'column', dataType: 'boolean' },
    { property: 'vendorZip', type: 'column', dataType: 'number' },
    { property: 'earlyTerminationClause', type: 'column', dataType: 'boolean' },
    { property: 'createdAt', type: 'column', dataType: 'date' },
    { property: 'updatedAt', type: 'column', dataType: 'date' },
    { property: 'renewalDate', type: 'column', dataType: 'date' },
    { property: 'renewalCount', type: 'column', dataType: 'number' },
    { property: 'status', type: 'column', dataType: 'string' },
    { property: 'statusDate', type: 'column', dataType: 'date' },
    { property: 'dateToNotify', type: 'column', dataType: 'date' },
    { property: 'ExemptVendorId', type: 'column', dataType: 'number' },
    {
      property: 'ClientRegisteredVendorId',
      type: 'column',
      dataType: 'number'
    },
    { property: 'VendorId', type: 'column', dataType: 'number' },
    { property: 'CompanyId', type: 'column', dataType: 'number' },

    { property: 'vendorName', type: 'alias' },
    { property: 'vendorLegalName', type: 'alias' },
    { property: 'contactFullName', type: 'alias' },

    { property: 'Vendor', model: 'Vendor', type: 'association' },
    { property: 'ExemptVendor', model: 'ExemptVendor', type: 'association' },
    { property: 'Files', model: 'File', type: 'association' },
    { property: 'SiteContact', model: 'SiteContact', type: 'association' },
    { property: 'VendorContact', model: 'VendorContact', type: 'association' },
    { property: 'Sites', model: 'Site', type: 'association' },
    { property: 'Company', model: 'Company', type: 'association' },
    {
      property: 'FrequencyPeriod',
      model: 'FrequencyPeriod',
      type: 'association'
    },
    {
      property: 'AssignedContractSite',
      model: 'AssignedContractSite',
      type: 'association'
    }
  ];

  static {
    Contract.ContractProperties.forEach((propertyObject) => {
      const { property, type } = propertyObject;
      if (type === 'column') {
        Contract.columnMap.set(property, propertyObject);
      }
      if (type === 'alias') {
        Contract.aliasedColumnMap.set(
          property,
          propertyObject as { property: string; type: 'alias' }
        );
      }
      if (type === 'association') {
        Contract.associationMap.set(
          property,
          propertyObject as {
            property: string;
            type: 'association';
            model: string;
          }
        );
      }
    });
  }

  constructor(ContractObject: Partial<Contract>) {
    super();

    Contract.ContractProperties.forEach((propertyObject) => {
      const { property } = propertyObject;
      const value = ContractObject[property];
      if (value !== undefined) {
        Object.assign(this, { [property]: value });
      }
    });
  }

  id?: number | null;
  startDate?: Date;
  expirationDate?: Date | null;
  contractName?: string | null;
  cancellationReminderDate?: Date | null;
  cancellationDeliveryMethod?:
    ('Certified Mail' | 'Standard Mail' | 'E-Mail' | 'Fax') | null;
  totalContractValue?: number | null;
  contractFrequency?: number | null;
  autoRenewing?: boolean | null;
  renewalTerm?: number | null;
  clientFirstName?: string | null;
  clientLastName?: string | null;
  clientEmail?: string | null;
  clientPhone?: string | null;
  isFinalized?: boolean;
  vendorFirstName?: string | null;
  vendorLastName?: string | null;
  vendorCellPhone?: string | null;
  vendorWorkPhone?: string | null;
  vendorEmail?: string | null;
  vendorContract?: string | null;
  vendorContract2?: string | null;
  vendorCity?: string | null;
  vendorState?: number | null;
  viveVendor?: boolean | null;
  vendorZip?: number | null;
  earlyTerminationClause?: boolean | null;
  createdAt?: Date | null;
  updatedAt?: Date | null;
  renewalDate?: Date | null;
  renewalCount?: number | null;
  status?:
    | (
        | 'NEW'
        | 'ARCHIVED'
        | 'EXPIRES90'
        | 'EXPIRES30'
        | 'EXPIRED'
        | 'RENEW90'
        | 'RENEW30'
        | 'RENEWED'
        | 'CANCELLED'
      )
    | null;
  statusDate?: Date | null;
  dateToNotify?: Date | null;
  ExemptVendorId?: number | null;
  ClientRegisteredVendorId?: number | null;
  VendorId?: number | null;
  CompanyId?: number | null;

  vendorName?: string | null;
  vendorLegalName?: string | null;
  contactFullName?: string;

  Vendor?: Vendor | null;
  ExemptVendor?: ExemptVendor | null;
  ClientRegisteredVendor?: ClientRegisteredVendor | null;
  Files?: File[];
  Contacts?: Contact[];
  SiteContact?: SiteContact;
  VendorContact?: VendorContact;
  Sites?: Site[];
  Company?: Company;
  FrequencyPeriod?: FrequencyPeriod;
  AssignedContractSite?: ContractSite;
}
